import React, { useState, useCallback, useEffect, useRef } from 'react';
import PreviewPanel from '../Preview/PreviewPanel';
import SummaryPanel from '../Chat/SummaryPanel';
import CampaignDetails from '../Chat/CampaignDetails';
import { useAuth } from '../../context/AuthContext';
import { canViewAction, getVisibleActions } from '../../utils/taskAccess';
import { useSocket } from '../../context/SocketContext';
import { format } from 'date-fns';
import toast from 'react-hot-toast';
import TaskDetailsModal from './TaskDetailsModal';
import { groupsAPI, authAPI, tasksAPI, getFileUrl, getFileDownloadUrl } from '../../utils/api';

export const TASK_TYPES={
  initial_setup:{label:'Initial Setup',icon:'🚀',color:'#a855f7'},
  share_link:   {label:'Share Link',   icon:'🔗',color:'#4f7dff'},
  pause_pid:    {label:'Pause PID',    icon:'⏸️',color:'#f59e0b'},
  raise_request:{label:'Raise Request',icon:'📋',color:'#22c55e'},
  optimise:     {label:'Optimise',     icon:'⚡',color:'#06b6d4'},
};

export const PAUSE_SCENARIOS=[
  'Low Quality Traffic','Fraud Detected','Budget Exhausted',
  'Geo Mismatch','KPI Not Met','Technical Issue','Advertiser Request','Other', 'Not Live', 'Not Doable',
  'Not Converting', 'Link Not Correct', 'Link Not Working', 'Link Not as Requested', 'Need RT Budget','Need UA Budget'
];

export const REQUEST_TYPES=['geo','budget','creative','technical','other'];

export const OPTIMISE_SCENARIOS=[
  'Decrease Budget','Increase Budget','Change Targeting','Update Creative',
  'Pause Campaign','Resume Campaign','Adjust Bidding','New Campaign Setup',
  'Optimise CTI','Optimise ITE','Resume Traffic','Take live','Optimise Install Fraud',
  'Optimise Event Fraud','Optimise CR','Pause'
];

export const FA_OPTIONS = ['FA1', 'FA2', 'FA3', 'FA4'];

// Role-based field definitions for optimise task
const OPTIMISE_FIELDS = {
  admin: ['assigned_to', 'pub_id', 'pid', 'fp', 'fa', 'optimise_scenario', 'attachment', 'note'],
  advertiser: ['assigned_to', 'pub_id', 'pid', 'fa', 'optimise_scenario', 'attachment', 'note'],
  advertiser_manager: ['assigned_to', 'pub_id', 'pid', 'fa', 'optimise_scenario', 'attachment', 'note'],
  adv_executive: ['assigned_to', 'pub_id', 'pid', 'fa', 'optimise_scenario', 'attachment', 'note'],
  publisher: ['assigned_to', 'pub_id', 'pid', 'fp', 'optimise_scenario', 'attachment', 'note'],
  publisher_manager: ['assigned_to', 'pub_id', 'pid', 'fp', 'optimise_scenario', 'attachment', 'note'],
  pub_executive: ['assigned_to', 'pub_id', 'pid', 'fp', 'optimise_scenario', 'attachment', 'note'],
  optimization: ['assigned_to', 'pub_id', 'pid', 'fp', 'optimise_scenario', 'attachment', 'note'],
  operations: ['assigned_to', 'pub_id', 'pid', 'fp', 'optimise_scenario', 'attachment', 'note'],
  operation_manager: ['assigned_to', 'pub_id', 'pid', 'fp', 'optimise_scenario', 'attachment', 'note'],
  am: ['assigned_to', 'pub_id', 'pid', 'fp', 'optimise_scenario', 'attachment', 'note']
};

const taskDraftKey = (userId, groupId) => `task_draft_${userId}_${groupId}`;

const serializeForm = (form) => {
  const clean = { ...form };
  if (clean.attachment instanceof File) clean.attachment = null;
  clean.optimise_entries = (clean.optimise_entries || []).map(e => {
    const ec = { ...e };
    if (ec.attachment instanceof File) ec.attachment = null;
    return ec;
  });
  return clean;
};

const isDirtyForm = (form) => {
  if (form.description?.trim()) return true;
  if (form.request_details?.trim()) return true;
  if ((form.entries || []).some(e => e.pub_id || e.pid || e.link || e.assigned_to?.length)) return true;
  if ((form.pause_entries || []).some(e => e.pub_id || e.pid || e.pause_reason || e.tag || e.assigned_to?.length)) return true;
  if ((form.optimise_entries || []).some(e => e.pub_id || e.pid || e.fp || e.fa || e.optimise_scenario || e.assigned_to?.length)) return true;
  return false;
};

const emptyForm=(userRole, type=null)=>{
  // If no type specified, use the first available task type for the user's role
  if (!type) {
    const visibleActions = getVisibleActions(userRole);
    type = visibleActions.length > 0 ? visibleActions[0] : 'share_link';
  }
  
  return({
    task_type:type, description:'',
    entries: [{ pub_id:'', pid:'', link:'', assigned_to:[], note:'' , geo:''}],
    pause_entries: [{ pub_id:'', pid:'', assigned_to:[], tag:'', pause_reason:'', geo:'', note:'' }],
    optimise_entries: [{
      assigned_to:[], pub_id:'', pid:'', fp:'', fa:'', f1:'', f2:'', optimise_scenario:'', attachment:null, note:''
    }],  pause_reason:'', request_type:'geo', request_details:'', cap_management:'',
    fp:'', f1:'', f2:'', optimise_scenario:'', attachment:null,
  });
};

/* ── Task item ─────────────────────────────────────────────── */
function TaskItem({task,currentUser,onStatusUpdate,onFollowup,onTaskClick,followups=[]}){
  const [expanded,setExpanded]=useState(false);
  const [comment,setComment]=useState('');
  const [busy,setBusy]=useState(false);
  const [showFollowupInput,setShowFollowupInput]=useState(false);
  const [followupMsg,setFollowupMsg]=useState('');
  const [followupBusy,setFollowupBusy]=useState(false);
  const type=TASK_TYPES[task.task_type]||TASK_TYPES.initial_setup;
  const sc={pending:'#f59e0b',accepted:'#4f7dff',completed:'#22c55e',rejected:'#ef4444'};

  const isAssignedUser = task.assigned_to === currentUser.id;
  const isTaskCreator = task.assigned_by === currentUser.id;

  const taskFollowups = followups.filter(f => f.task_id === task.id);

  const handleFollowupSubmit = async () => {
    if (!followupMsg.trim()) return;
    setFollowupBusy(true);
    try {
      await onFollowup(task, followupMsg.trim());
      setFollowupMsg('');
      setShowFollowupInput(false);
    } catch { /* toast handled in parent */ }
    setFollowupBusy(false);
  };

  // null | 'reject' (needs a reason) | 'accept' / 'complete' (optimise tasks: further scope, optional / required)
  const [noteMode,setNoteMode]=useState(null);
  const [furtherScope,setFurtherScope]=useState('');
  const isOptimise = task.task_type==='optimise';

  const resetNotes=()=>{
    setComment('');setExpanded(false);setNoteMode(null);
    setFurtherScope('');
  };

  const doStatus=async(status,feedbackPayload)=>{
    setBusy(true);
    try{await onStatusUpdate(task.id,status,comment,feedbackPayload);toast.success(`Task ${status}`);resetNotes();}
    catch{/* toast handled in parent */}
    setBusy(false);
  };

  const startAccept=()=>{
    if(isOptimise)setNoteMode('accept');
    else doStatus('accepted');
  };

  const startComplete=()=>{
    if(isOptimise){setNoteMode('complete');setExpanded(false);}
    else doStatus('completed');
  };

  const handleTaskClick = () => {
    if (onTaskClick) {
      onTaskClick(task.id);
    }
  };

  

  return(
    <div
      id={`task-${task.id}`}
      style={{marginBottom:10,borderRadius:10,background:'var(--bg-secondary)',border:'1px solid var(--border)',borderLeft:`3px solid ${type.color}`,padding:'12px 14px',cursor:'pointer',transition:'all 0.2s'}}
      // onClick={handleTaskClick}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateX(4px)';
        e.currentTarget.style.boxShadow = 'var(--shadow-md)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateX(0)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      <div style={{display:'flex',gap:10}}>
        <span style={{fontSize:20}}>{type.icon}</span>
        <div style={{flex:1,minWidth:0}}>
          <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap',marginBottom:4}}>
            <span style={{fontSize:10,fontWeight:700,padding:'2px 7px',borderRadius:20,background:`${sc[task.status]}20`,color:sc[task.status],border:`1px solid ${sc[task.status]}40`,textTransform:'uppercase',letterSpacing:'.5px'}}>{task.status}</span>
            <span style={{fontSize:10,padding:'2px 7px',borderRadius:20,background:`${type.color}15`,color:type.color}}>{type.label}</span>
          </div>
          {task.description&&<div style={{fontSize:12,color:'var(--text-secondary)',marginBottom:6}}>{task.description}</div>}
          <div style={{display:'flex',gap:12,fontSize:11,color:'var(--text-muted)',marginBottom:8,flexWrap:'wrap'}}>
            <span>By {task.assigned_by_name}</span>
            {task.assigned_to_name&&<span>→ {task.assigned_to_name}</span>}
            <span style={{marginLeft:'auto'}}>{format(new Date(task.created_at),'MMM d, HH:mm')}</span>
          </div>
          {/* extra fields */}
          {(task.pub_id||task.pid||task.link||task.geo||task.note||task.pause_reason||task.fp||task.optimise_scenario)&&(
            <div style={{background:'var(--bg-active)',borderRadius:8,padding:'8px 10px',marginBottom:8,fontSize:12}}>
              {task.pub_id&&<div style={{marginBottom:2}}><span style={{color:'var(--text-muted)'}}>PubID: </span><code style={{color:'var(--accent)'}}>{task.pub_id}</code></div>}
              {task.pid&&<div style={{marginBottom:2}}><span style={{color:'var(--text-muted)'}}>PID: </span><code style={{color:'var(--accent)'}}>{task.pid}</code></div>}
              {task.task_type==='share_link'&&task.geo&&<div style={{marginBottom:2}}><span style={{color:'var(--text-muted)'}}>GEO: </span><span style={{color:'var(--accent)',fontWeight:600}}>{task.geo}</span></div>}
              {(task.task_type==='share_link'||task.task_type==='pause_pid'||task.task_type==='optimise')&&task.note&&<div style={{marginBottom:2}}><span style={{color:'var(--text-muted)'}}>Note: </span><span style={{color:'var(--text-primary)'}}>{task.note}</span></div>}
              {task.link&&<div style={{marginBottom:2}}><span style={{color:'var(--text-muted)'}}>Link: </span><a href={task.link} target="_blank" rel="noreferrer" style={{color:'var(--accent)',wordBreak:'break-all'}}>{task.link}</a></div>}
              {task.pause_reason&&<div style={{color:'#f59e0b'}}>⚠️ {task.pause_reason}</div>}
              {task.request_type&&<div><span style={{color:'var(--text-muted)'}}>Request: </span><strong style={{textTransform:'uppercase'}}>{task.request_type}</strong>{task.request_details ? ` – ${task.request_details}` : ''}</div>}
              {task.cap_management&&<div><span style={{color:'var(--text-muted)'}}>Cap Management: </span>{task.cap_management}</div>}
              {task.fp&&<div><span style={{color:'var(--text-muted)'}}>FP: </span>{task.fp}</div>}
              {task.f1&&<div><span style={{color:'var(--text-muted)'}}>F1: </span>{task.f1}</div>}
              {task.f2&&<div><span style={{color:'var(--text-muted)'}}>F2: </span>{task.f2}</div>}
              {task.optimise_scenario&&<div><span style={{color:'var(--text-muted)'}}>Scenario: </span><span style={{color:'#06b6d4'}}>{task.optimise_scenario}</span></div>}
              {task.attachment_url&&(
                <a href={getFileDownloadUrl(task.attachment_url, task.attachment_name)} download={task.attachment_name} target="_blank" rel="noreferrer"
                  style={{display:'inline-flex',alignItems:'center',gap:4,color:'var(--accent)',fontSize:11,marginTop:4,textDecoration:'none',background:'var(--bg-primary)',padding:'3px 8px',borderRadius:6,border:'1px solid var(--border)'}}>
                  📎 {task.attachment_name||'Attachment'} ⬇
                </a>
              )}
            </div>
          )}
          {/* rejection info */}
          {task.status === 'rejected' && (task.rejected_by_name || task.rejection_note) && (
            <div style={{background:'#ef444415',border:'1px solid #ef444430',borderRadius:8,padding:'7px 10px',marginBottom:8,fontSize:12}}>
              {task.rejected_by_name && <div style={{marginBottom:2}}><span style={{color:'#ef4444',fontWeight:600}}>Rejected by: </span><span style={{color:'var(--text-primary)'}}>{task.rejected_by_name}</span></div>}
              {task.rejection_note && <div><span style={{color:'#ef4444',fontWeight:600}}>Note: </span><span style={{color:'var(--text-secondary)'}}>{task.rejection_note}</span></div>}
            </div>
          )}
          {/* completion feedback */}
          {task.status === 'completed' && task.completion_note && (
            <div style={{background:'#22c55e15',border:'1px solid #22c55e30',borderRadius:8,padding:'7px 10px',marginBottom:8,fontSize:12}}>
              {task.completed_by_name && <div style={{marginBottom:2}}><span style={{color:'#22c55e',fontWeight:600}}>Completed by: </span><span style={{color:'var(--text-primary)'}}>{task.completed_by_name}</span></div>}
              <div style={{whiteSpace:'pre-wrap',color:'var(--text-secondary)'}}>{task.completion_note}</div>
            </div>
          )}
          {/* actions - Only show for assigned users */}
          {isAssignedUser && task.status==='pending'&&!noteMode&&(
            <div style={{display:'flex',gap:6,flexWrap:'wrap',marginTop:4}}>
              <button className="btn btn-xs btn-success" onClick={startAccept} disabled={busy}>✓ Accept</button>
              <button className="btn btn-xs btn-danger"  onClick={()=>{setNoteMode('reject');setExpanded(false);}} disabled={busy}>✗ Reject</button>
              <button className="btn btn-xs btn-primary" onClick={startComplete} disabled={busy}>✔ Complete</button>
              <button className="btn-icon" style={{padding:'3px 8px',fontSize:11,border:'1px solid var(--border)',borderRadius:6}} onClick={()=>setExpanded(x=>!x)}>
                {expanded?'▲':'+ Note'}
              </button>
            </div>
          )}
          {isAssignedUser && task.status==='accepted'&&!noteMode&&(
            <button className="btn btn-xs btn-success" style={{marginTop:6}} onClick={startComplete} disabled={busy}>✔ Mark Complete</button>
          )}
          {isAssignedUser && expanded&&!noteMode&&<textarea className="form-control" style={{minHeight:56,fontSize:12,marginTop:8}} placeholder="Comment…" value={comment} onChange={e=>setComment(e.target.value)}/>}
          {/* mandatory rejection reason */}
          {isAssignedUser && noteMode==='reject' && (
            <div style={{marginTop:8,display:'flex',flexDirection:'column',gap:6}}>
              <label style={{fontSize:11,fontWeight:600,color:'#ef4444'}}>Reason for rejection *</label>
              <textarea className="form-control" style={{minHeight:56,fontSize:12}} maxLength={2000} autoFocus
                placeholder="Explain why this task is being rejected…" value={comment} onChange={e=>setComment(e.target.value)}/>
              <div style={{display:'flex',gap:6}}>
                <button className="btn btn-xs btn-secondary" onClick={resetNotes} disabled={busy}>Cancel</button>
                <button className="btn btn-xs btn-danger" onClick={()=>doStatus('rejected')} disabled={busy||!comment.trim()}>
                  {busy?'Rejecting…':'✗ Confirm Reject'}
                </button>
              </div>
            </div>
          )}
          {isAssignedUser && (noteMode==='accept'||noteMode==='complete') && (
            <div style={{marginTop:8,display:'flex',flexDirection:'column',gap:6}}>
              <label style={{fontSize:11,fontWeight:600,color:'#22c55e'}}>
                Further scope for improvement {noteMode==='complete'?'*':'(optional)'}
              </label>
              <textarea className="form-control" style={{minHeight:56,fontSize:12}} maxLength={1000} autoFocus
                placeholder="Any further optimisation possible?" value={furtherScope} onChange={e=>setFurtherScope(e.target.value)}/>
              <div style={{display:'flex',gap:6}}>
                <button className="btn btn-xs btn-secondary" onClick={resetNotes} disabled={busy}>Cancel</button>
                {noteMode==='accept' ? (
                  <button className="btn btn-xs btn-success" onClick={()=>doStatus('accepted',{further_scope:furtherScope})} disabled={busy}>
                    {busy?'Accepting…':'✓ Confirm Accept'}
                  </button>
                ) : (
                  <button className="btn btn-xs btn-success" onClick={()=>doStatus('completed',{further_scope:furtherScope})}
                    disabled={busy||!furtherScope.trim()}>
                    {busy?'Completing…':'✔ Confirm Complete'}
                  </button>
                )}
              </div>
            </div>
          )}
          {!isAssignedUser && isTaskCreator && (
            <div style={{fontSize:11,color:'var(--text-muted)',marginTop:6,fontStyle:'italic'}}>
              You can only view this task. Actions are available to the assigned user.
            </div>
          )}
          {/* Follow-up thread */}
          {taskFollowups.length > 0 && (
            <div style={{marginTop:8,borderLeft:'2px solid var(--border)',paddingLeft:10}}>
              {taskFollowups.map(f=>(
                <div key={f.id} style={{marginBottom:6,fontSize:11}}>
                  <span style={{fontWeight:600,color:'var(--text-primary)'}}>{f.created_by_name}</span>
                  <span style={{color:'var(--text-muted)',marginLeft:6}}>{format(new Date(f.created_at),'MMM d, HH:mm')}</span>
                  {f.recipient_name&&<span style={{color:'var(--text-muted)',marginLeft:4}}>→ {f.recipient_name}</span>}
                  <div style={{color:'var(--text-secondary)',marginTop:2}}>{f.message}</div>
                </div>
              ))}
            </div>
          )}
          {/* Follow-up button — visible to assigner and assignee */}
          {(isAssignedUser||isTaskCreator)&&(task.status==='completed'||task.status==='rejected')&&(
            showFollowupInput ? (
              <div style={{marginTop:8,display:'flex',flexDirection:'column',gap:6}}>
                <textarea
                  className="form-control"
                  style={{minHeight:52,fontSize:12}}
                  placeholder="Type your follow-up message…"
                  value={followupMsg}
                  onChange={e=>setFollowupMsg(e.target.value)}
                  onKeyDown={e=>{if(e.key==='Enter'&&!e.shiftKey){e.preventDefault();handleFollowupSubmit();}if(e.key==='Escape')setShowFollowupInput(false);}}
                  autoFocus
                />
                <div style={{display:'flex',gap:6}}>
                  <button className="btn btn-xs btn-secondary" onClick={()=>setShowFollowupInput(false)}>Cancel</button>
                  <button className="btn btn-xs btn-primary" onClick={handleFollowupSubmit} disabled={followupBusy||!followupMsg.trim()}>
                    {followupBusy?'Sending…':'Send Follow-up'}
                  </button>
                </div>
              </div>
            ) : (
              <button className="btn btn-xs btn-secondary" style={{marginTop:6}} onClick={()=>setShowFollowupInput(true)}>↩ Follow Up</button>
            )
          )}
          {/* Details button - always show */}
          <button 
            className="btn btn-xs btn-info" 
            style={{marginTop:6}} 
            onClick={() => onTaskClick(task.id)}
            title="View task details"
          >
            📋 Details
          </button>
        </div>
      </div>
    </div>
  );
}

export default function TasksPanel({group, taskTarget, searchQuery=''}){
  const {user}=useAuth();
  const {on}=useSocket();
  const [tasks,setTasks]=useState([]);
  const [followups,setFollowups]=useState([]);
  const [filter,setFilter]=useState('all');
  const [showCreate,setShowCreate]=useState(()=>{
    if(!user?.id||!group?.id) return false;
    return !!localStorage.getItem(taskDraftKey(user.id,group.id));
  });
  const [openAssigneeKey,setOpenAssigneeKey]=useState(null);
  const [dropdownPos,    setDropdownPos]    =useState({top:0,left:0,width:0});

  useEffect(() => {
    if (!openAssigneeKey) return;
    const close = () => setOpenAssigneeKey(null);
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [openAssigneeKey]);
  // const [refreshKey, setRefreshKey] = useState(0); // Add refresh key for forcing re-renders
  const [loadingTasks, setLoadingTasks] = useState(false); // Add loading state for tasks
  const [users,setUsers]=useState([]);
  const [members,setMembers]=useState([]);
  const [form,setForm]=useState(emptyForm(user?.role));
  const [creating,setCreating]=useState(false);
  const [uploadingFile, setUploadingFile] = useState(null);
  const hasRestoredRef = useRef(false);
  const [hasDraft, setHasDraft] = useState(false);

  // File upload function for tasks (no auth required)
  const handleTaskFileUpload = async (file) => {
    if (file.size > 150 * 1024 * 1024) {
      toast.error(`${file.name} exceeds 150MB limit`);
      return null;
    }
    try {
      setUploadingFile(file.name);
      
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/tasks/upload`, {
        method: 'POST',
        body: formData,
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Upload failed');
      }
      
      const result = await response.json();
      return result.file;
    } catch (error) {
      toast.error(error.message || 'Failed to upload file');
      return null;
    } finally {
      setUploadingFile(null);
    }
  };
  const [selectedTask, setSelectedTask] = useState(null);
  const [taskDetails, setTaskDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const fileRef=useRef(null);
  const f=(k,v)=>setForm(p=>({...p,[k]:v}));

  const [assigneeSearch, setAssigneeSearch] = useState('');

  const openAssignee = (e, key) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setDropdownPos({ top: rect.bottom + 2, left: rect.left, width: rect.width });
    setAssigneeSearch('');
    setOpenAssigneeKey(prev => prev === key ? null : key);
  };
  const toggleAssignee = (ids, userId) => {
    const num = Number(userId);
    return ids.includes(num) ? ids.filter(id => id !== num) : [...ids, num];
  };
  const assigneeLabel = (ids) => {
    if (!ids || ids.length === 0) return 'Unassigned';
    if (ids.length === 1) return users.find(u => u.id === ids[0])?.full_name || 'User';
    return `${ids.length} users`;
  };

  // Add CSS animation for spinner
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Role-based filtering for "Assign To" dropdowns
  const getFilteredMembersForTaskType = useCallback((taskType) => {
    if (!members || members.length === 0) return [];
    
    switch(taskType) {
      case 'share_link':
        return members.filter(member =>
          (member.role === 'publisher' || member.role === 'publisher_manager' || member.role === 'pub_executive' || member.role === 'optimization') &&
          member.id !== user?.id
        );

      case 'pause_pid':
        if (['pub_executive', 'publisher', 'publisher_manager'].includes(user?.role)) {
          return members.filter(member =>
            (member.role === 'optimization' || member.role === 'operations' || member.role === 'operation_manager' || member.role === 'advertiser' || member.role === 'advertiser_manager' || member.role === 'adv_executive') &&
            member.id !== user?.id
          );
        }
        return members.filter(member =>
          (member.role === 'publisher' || member.role === 'publisher_manager' || member.role === 'pub_executive' || member.role === 'optimization' || member.role === 'operations' || member.role === 'operation_manager') &&
          member.id !== user?.id
        );
      
      case 'raise_request':
        // Show only advertiser and advertiser_manager (excluding current user)
        return members.filter(member => 
          (member.role === 'advertiser' || member.role === 'advertiser_manager' || member.role === 'adv_executive') &&
          member.id !== user?.id
        );
      
      case 'optimise':
        // Show all group members (excluding current user)
        return members.filter(member => member.id !== user?.id);
      
      default:
        // For initial_setup and others, show all members (excluding current user)
        return members.filter(member => member.id !== user?.id);
    }
  }, [members, user?.id]);

  // API call to get group members
  useEffect(() => {
    if (!group) return;
    groupsAPI.getById(group.id).then(d => setMembers(d.members || []));
    authAPI.getUsers().then(d => setUsers(d.users || []));
  }, [group?.id]);

  // Entry management functions
  // const addEntry = () => {
  //   setForm(p => ({
  //     ...p,
  //     entries: [...p.entries, { pub_id:'', pid:'', link:'', assigned_to:'', note:'' }]
  //   }));
  // };
  const addEntry = () => {
  setForm(p => {
    const lastEntry = p.entries[p.entries.length - 1];

    const newEntry = lastEntry
      ? {
          ...lastEntry,
          link: ''     // reset link
          // note: ''       // reset note
        }
      : { pub_id:'', pid:'', link:'', assigned_to:[], note:'' };

    return {
      ...p,
      entries: [...p.entries, newEntry]
    };
  });
};

  const removeEntry = (index) => {
    setForm(p => ({
      ...p,
      entries: p.entries.filter((_, i) => i !== index)
    }));
  };

  const updateEntry = (index, field, value) => {
    setForm(p => ({
      ...p,
      entries: p.entries.map((entry, i) => 
        i === index ? { ...entry, [field]: value } : entry
      )
    }));
  };

  // Pause entry management functions
  // const addPauseEntry = () => {
  //   setForm(p => ({
  //     ...p,
  //     pause_entries: [...p.pause_entries, { pub_id:'', pid:'', assigned_to:'', pause_reason:'' }]
  //   }));
  // };
  
  const addPauseEntry = () => {
  setForm(p => {
    const lastEntry = p.pause_entries[p.pause_entries.length - 1];

    const newEntry = lastEntry
      ? {
          ...lastEntry,
          pause_reason: '',
          tag: ''
        }
      : { pub_id:'', pid:'', assigned_to:[], tag:'', pause_reason:'', geo:'', note:'' };

    return {
      ...p,
      pause_entries: [...p.pause_entries, newEntry]
    };
  });
};

  const removePauseEntry = (index) => {
    setForm(p => ({
      ...p,
      pause_entries: p.pause_entries.filter((_, i) => i !== index)
    }));
  };

  const updatePauseEntry = (index, field, value) => {
    setForm(p => ({
      ...p,
      pause_entries: p.pause_entries.map((entry, i) => 
        i === index ? { ...entry, [field]: value } : entry
      )
    }));
  };

  // Optimise entry management functions
  // const addOptimiseEntry = () => {
  //   setForm(p => ({
  //     ...p,
  //     optimise_entries: [...p.optimise_entries, { assigned_to:'', pub_id:'', pid:'', fp:'', fa:'', f1:'', f2:'', optimise_scenario:'', attachment:null }]
  //   }));
  // };
  const addOptimiseEntry = () => {
  setForm(p => {
    const lastEntry = p.optimise_entries[p.optimise_entries.length - 1];

    const newEntry = lastEntry
      ? {
          ...lastEntry,
          attachment: null,
          optimise_scenario: '',
          note: ''
        }
      : {
          assigned_to:[], pub_id:'', pid:'',
          fp:'', fa:'', f1:'', f2:'',
          optimise_scenario:'', attachment:null, note:''
        };

    return {
      ...p,
      optimise_entries: [...p.optimise_entries, newEntry]
    };
  });
};

  const removeOptimiseEntry = (index) => {
    setForm(p => ({
      ...p,
      optimise_entries: p.optimise_entries.filter((_, i) => i !== index)
    }));
  };

  const updateOptimiseEntry = (index, field, value) => {
    setForm(p => ({
      ...p,
      optimise_entries: p.optimise_entries.map((entry, i) => 
        i === index ? { ...entry, [field]: value } : entry
      )
    }));
  };

  const renderEntryField = (field, entry, entryIndex) => {
                switch(field) {
                  case 'assigned_to':
                    return (
                      // <select 
                      //   className="form-control" 
                      //   style={{fontSize:11,padding:4,background:'rgba(255,255,255,0.1)',border:'1px solid rgba(255,255,255,0.2)',color:'#fff'}}
                      //   value={entry.assigned_to} 
                      //   onChange={e => updateOptimiseEntry(entryIndex, 'assigned_to', e.target.value)}
                      // >
                      //   {/* <option value="">Unassigned</option> */}
                      //   {getFilteredMembersForTaskType('optimise').map(member => <option key={member.id} value={member.id}>{member.full_name}</option>)}
                      // </select>
                      <div key="assigned_to" style={{position:'relative'}}>
                        <button type="button"
                          onClick={e=>openAssignee(e,`opt-${entryIndex}`)}
                          style={{width:'100%',fontSize:11,padding:4,background:'var(--bg-hover)',border:(entry.assigned_to||[]).length===0?'1px solid #ef4444':'1px solid var(--border)',color:'var(--text-primary)',borderRadius:4,cursor:'pointer',textAlign:'left',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                          {assigneeLabel(entry.assigned_to)}
                        </button>
                        {openAssigneeKey===`opt-${entryIndex}`&&(
                          <div onMouseDown={e=>e.stopPropagation()} style={{position:'fixed',top:dropdownPos.top,left:dropdownPos.left,minWidth:Math.max(dropdownPos.width,160),zIndex:9999,background:'var(--bg-secondary)',border:'1px solid var(--border)',borderRadius:6,padding:4,boxShadow:'0 4px 16px rgba(0,0,0,0.15)',display:'flex',flexDirection:'column',maxHeight:220}}>
                            <input autoFocus value={assigneeSearch} onChange={e=>setAssigneeSearch(e.target.value)} placeholder="Search…" onMouseDown={e=>e.stopPropagation()} style={{fontSize:11,padding:'3px 6px',marginBottom:4,border:'1px solid var(--border)',borderRadius:4,background:'var(--bg-primary)',color:'var(--text-primary)',outline:'none',flexShrink:0}}/>
                            <div style={{overflowY:'auto',flex:1}}>
                            {getFilteredMembersForTaskType('optimise').filter(u=>u.full_name.toLowerCase().includes(assigneeSearch.toLowerCase())).map(u=>(
                              <label key={u.id} style={{display:'flex',alignItems:'center',gap:6,padding:'3px 6px',cursor:'pointer',borderRadius:4,fontSize:11,color:'var(--text-primary)'}}>
                                <input type="checkbox" checked={(entry.assigned_to||[]).includes(Number(u.id))}
                                  onChange={()=>updateOptimiseEntry(entryIndex,'assigned_to',toggleAssignee(entry.assigned_to||[],u.id))}/>
                                {u.full_name}
                              </label>
                            ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  case 'pub_id':
                    return (
                      <input 
                        className="form-control" 
                        style={{fontSize:11,padding:4,background:'rgba(255,255,255,0.1)',border:'1px solid rgba(255,255,255,0.2)'}} 
                        placeholder="PubID" 
                        value={entry.pub_id} 
                        onChange={e => updateOptimiseEntry(entryIndex, 'pub_id', e.target.value)}
                      />
                    );
                  case 'pid':
                    return (
                      <input 
                        className="form-control" 
                        style={{fontSize:11,padding:4,background:'rgba(255,255,255,0.1)',border:'1px solid rgba(255,255,255,0.2)'}} 
                        placeholder="PID" 
                        value={entry.pid} 
                        onChange={e => updateOptimiseEntry(entryIndex, 'pid', e.target.value)}
                      />
                    );
                  case 'fp':
                    return (
                      <input 
                        className="form-control" 
                        style={{fontSize:11,padding:4,background:'rgba(255,255,255,0.1)',border:'1px solid rgba(255,255,255,0.2)'}} 
                        placeholder="FP" 
                        value={entry.fp} 
                        onChange={e => updateOptimiseEntry(entryIndex, 'fp', e.target.value)}
                      />
                    );
                  case 'fa':
                    return (
                      <select 
                        className="form-control" 
                        style={{fontSize:11,padding:4,background:'rgba(255,255,255,0.1)',border:'1px solid rgba(255,255,255,0.2)'}}
                        value={entry.fa} 
                        onChange={e => updateOptimiseEntry(entryIndex, 'fa', e.target.value)}
                      >
                        <option value="">Select FA</option>
                        {FA_OPTIONS.map(fa => <option key={fa} value={fa}>{fa}</option>)}
                      </select>
                    );
                  case 'f1':
                    return (
                      <input 
                        className="form-control" 
                        style={{fontSize:11,padding:4,background:'rgba(255,255,255,0.1)',border:'1px solid rgba(255,255,255,0.2)'}} 
                        placeholder="F1" 
                        value={entry.f1} 
                        onChange={e => updateOptimiseEntry(entryIndex, 'f1', e.target.value)}
                      />
                    );
                  case 'f2':
                    return (
                      <input 
                        className="form-control" 
                        style={{fontSize:11,padding:4,background:'rgba(255,255,255,0.1)',border:'1px solid rgba(255,255,255,0.2)'}} 
                        placeholder="F2" 
                        value={entry.f2} 
                        onChange={e => updateOptimiseEntry(entryIndex, 'f2', e.target.value)}
                      />
                    );
                  case 'optimise_scenario':
                    return (
                      <select 
                        className="form-control" 
                        style={{fontSize:11,padding:4,background:'rgba(255,255,255,0.1)',border:'1px solid rgba(255,255,255,0.2)'}}
                        value={entry.optimise_scenario} 
                        onChange={e => updateOptimiseEntry(entryIndex, 'optimise_scenario', e.target.value)}
                      >
                        <option value="">Select scenario…</option>
                        {OPTIMISE_SCENARIOS.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    );
                  case 'attachment':
                    return (
                      <div style={{display:'flex',flexDirection:'column',alignItems:'stretch',gap:2}}>
                        <input
                          type="file"
                          style={{display:'none'}}
                          id={`file-${entryIndex}`}
                          onChange={async e => {
                            const file = e.target.files[0];
                            if (file) {
                              const uploadedFile = await handleTaskFileUpload(file);
                              if (uploadedFile) {
                                updateOptimiseEntry(entryIndex, 'attachment', uploadedFile.url);
                                updateOptimiseEntry(entryIndex, 'attachment_name', uploadedFile.originalname);
                              }
                            }
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => document.getElementById(`file-${entryIndex}`).click()}
                          style={{
                            background:'rgba(255,255,255,0.2)',
                            border:'1px solid rgba(255,255,255,0.3)',
                            borderRadius:'4px',
                            padding:'4px 8px',
                            fontSize:'10px',
                            cursor:'pointer',
                            width:'100%'
                          }}
                        >
                          📎 Choose
                        </button>
                        {entry.attachment && (
                          <span style={{fontSize:9,color:'rgba(255,255,255,0.7)',display:'flex',alignItems:'center',gap:2}}>
                            <span style={{overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',flex:1}}>
                              {entry.attachment_name || entry.attachment.split('/').pop()}
                            </span>
                            <button
                              type="button"
                              onClick={() => {
                                updateOptimiseEntry(entryIndex, 'attachment', null);
                                updateOptimiseEntry(entryIndex, 'attachment_name', null);
                              }}
                              style={{background:'none',border:'none',cursor:'pointer',color:'rgba(255,255,255,0.5)',flexShrink:0}}
                            >
                              ✕
                            </button>
                          </span>
                        )}
                      </div>
                    );
                  case 'note':
                    return (
                      <div style={{ position: "relative" }}>
                        <input
                          className="form-control"
                          style={{
                            fontSize: 11,
                            padding: 4,
                            background: "rgba(255,255,255,0.1)",
                            border: "1px solid rgba(255,255,255,0.2)",
                            cursor: "pointer"
                          }}
                          value={entry.note || ""}
                          placeholder="Click to add note"
                          readOnly
                          onClick={(e) => openOptimiseNoteEditor(entry, entryIndex, e)}
                        />
                        {openNoteIndex === entryIndex && (
                          <div
                            ref={noteWrapperRef}
                            style={{
                              position: "fixed",
                              top: notePopupPos.top,
                              left: notePopupPos.left,
                              width: 300,
                              background: "#1f1f1f",
                              border: "1px solid #333",
                              borderRadius: 8,
                              padding: 10,
                              zIndex: 9999,
                              boxShadow: "0 8px 20px rgba(0,0,0,0.4)"
                            }}
                          >
                            <textarea
                              value={tempNote}
                              onChange={(e) => setTempNote(e.target.value)}
                              style={{
                                width: "100%",
                                height: 100,
                                resize: "none",
                                borderRadius: 6,
                                border: "1px solid #444",
                                padding: 6,
                                background: "#111",
                                color: "#fff"
                              }}
                            />
                            <div style={{ marginTop: 8, textAlign: "right" }}>
                              <button
                                onClick={saveOptimiseNote}
                                style={{
                                  marginRight: 6,
                                  padding: "4px 10px",
                                  background: "#1677ff",
                                  color: "#fff",
                                  border: "none",
                                  borderRadius: 4
                                }}
                              >
                                Save
                              </button>
                              <button
                                onClick={() => setOpenNoteIndex(null)}
                                style={{
                                  padding: "4px 10px",
                                  background: "#444",
                                  color: "#fff",
                                  border: "none",
                                  borderRadius: 4
                                }}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  default:
                    return null;
                }
              };

  // const load=useCallback(async()=>{
  //   if(!group)return;
  //   setLoadingTasks(true);
  //   try {
  //     // Add small delay to make loading state visible during testing
  //     await new Promise(resolve => setTimeout(resolve, 500));
  //     const data=await tasksAPI.getByGroup(group.id);
  //     // setTasks(data.tasks||[]);
  //     setTasks((data.tasks || []).filter(t => t.group_id === group?.id));
  //   } catch (error) {
  //     console.error('Failed to load tasks:', error);
  //     setTasks([]);
  //   } finally {
  //     setLoadingTasks(false);
  //   }
  // },[group?.id]);// eslint-disable-line

  const load = useCallback(async () => {
  if (!group) return;

  console.log('[LOAD TASKS] Fetching for group:', group.id);

  setLoadingTasks(true);

  try {
    const data = await tasksAPI.getByGroup(group.id);

    console.log('[LOAD RESPONSE]', data.tasks);

    const filteredTasks = (data.tasks || []).filter(t => {
      const match = t.group_id === group?.id;

      if (!match) {
        console.warn('[API WRONG GROUP TASK]', t.id);
      }

      return match;
    });

    console.log('[FINAL TASKS AFTER LOAD]', filteredTasks);

    setTasks(filteredTasks);

  } catch (error) {
    console.error('[LOAD ERROR]', error);
    setTasks([]);
  } finally {
    setLoadingTasks(false);
  }
}, [group?.id]);

  useEffect(()=>{
    load();
    authAPI.getUsers().then(d=>setUsers(d.users||[]));
    if(group?.id) tasksAPI.getFollowups(group.id).then(d=>setFollowups(d.followups||[])).catch(()=>{});
  },[group?.id]);// eslint-disable-line

  // Draft restoration — runs on mount and group/user change
  useEffect(() => {
    if (!user?.id || !group?.id) return;
    const key = taskDraftKey(user.id, group.id);
    const raw = localStorage.getItem(key);
    if (raw) {
      try {
        const saved = JSON.parse(raw);
        setForm(saved);
        setShowCreate(true);
        setHasDraft(true);
      } catch {
        setForm(emptyForm(user?.role));
        setShowCreate(false);
        setHasDraft(false);
      }
    } else {
      setForm(emptyForm(user?.role));
      setShowCreate(false);
      setHasDraft(false);
    }
    hasRestoredRef.current = true;
    return () => { hasRestoredRef.current = false; };
  }, [user?.id, group?.id]); // eslint-disable-line

  // Draft save — fires whenever form changes, gated by hasRestoredRef (StrictMode-safe)
  useEffect(() => {
    if (!user?.id || !group?.id || !hasRestoredRef.current) return;
    const key = taskDraftKey(user.id, group.id);
    if (isDirtyForm(form)) {
      localStorage.setItem(key, JSON.stringify(serializeForm(form)));
      setHasDraft(true);
      window.dispatchEvent(new CustomEvent('task-draft-changed', { detail: { groupId: group.id } }));
    } else if (hasDraft) {
      // hasDraft guard: only remove when a draft was previously loaded and user cleared it.
      // Without this, StrictMode's first-pass save (form=emptyForm, no draft committed yet)
      // would delete the draft before the second-pass restoration can read it.
      localStorage.removeItem(key);
      setHasDraft(false);
      window.dispatchEvent(new CustomEvent('task-draft-changed', { detail: { groupId: group.id } }));
    }
  }, [form]); // eslint-disable-line

  useEffect(()=>{
    const unsub=on('task_update',({action,task,task_id,status,response,updated_by})=>{
 console.log('[SOCKET task_update]', {
  action,
  task_id,
  status,
  updated_by,
  currentGroup: group?.id
});
      
      if(action==='status_changed'){
        // Update task status in the list with single state update
        setTasks(prev => {
          const updatedTasks = prev.map(t => {
            if (t.id !== task_id) return t;
            const extra = status === 'rejected' && response
              ? { rejection_note: response.comment || null, rejected_by_name: response.user_name || null }
              : status === 'completed' && response
              ? { completion_note: response.comment || null, completed_by_name: response.user_name || null }
              : {};
            return { ...t, status, ...extra };
          });
          return updatedTasks;
        });
        // Force re-render
        // setRefreshKey(prev => prev + 1);
        
        // Show notification about the update
        if(response && response.comment){
          const actionText = status === 'accepted' ? 'accepted' : status === 'rejected' ? 'rejected' : 'completed';
          toast(`${updated_by} ${actionText} task${response.comment ? ' with note' : ''}`);
        }
        
        // If we have this task selected, update the details
        if(selectedTask === task_id && taskDetails){
          setTaskDetails(prev=>prev ? {...prev,status} : null);
        }
      }
      console.log('[TASK CREATED CHECK]', {
  taskId: task.id,
  taskGroup: task.group_id,
  currentGroup: group?.id,
  assigned_to: task.assigned_to,
  assigned_by: task.assigned_by,
  currentUser: user?.id
});
      if(action==='created' && task){
        // Only add if task is assigned to current user or created by current user
      if (
  (task.assigned_to === user?.id || task.assigned_by === user?.id) &&
  task.group_id === group?.id
){
          setTasks(prev => {
            // Avoid duplicates
            if(!prev.find(t => t.id === task.id)) {
              // Force re-render
              // setRefreshKey(prev => prev + 1);
              return [task,...prev];
            }
            return prev;
          });
        }
      }
    });
    return unsub;
  },[on,user?.id,selectedTask,taskDetails]);

  // Listen for real-time task assignments
  useEffect(()=>{
    const unsub=on('task_assigned',({task,subTasks,assigned_by,message,group_id})=>{
      console.log('[SOCKET task_assigned]', {
  task,
  subTasks,
  group_id,
  currentGroup: group?.id
});
      // Show toast notification for task assignment
      toast.success(message);
      
      // Only add task if it's for current user's group
      if(group_id === group?.id) {
        // Collect all tasks to add in a single state update
        const tasksToAdd = [];
        
        // Check if main task should be shown (creator or has assigned sub-tasks)
        const shouldShowMainTask = task && (
          task.assigned_to === user?.id || 
          task.assigned_by === user?.id ||
          (subTasks && subTasks.some(st => st.assigned_to === user?.id))
        );
        
        if(shouldShowMainTask) {
          tasksToAdd.push(task);
        }
        
        // Add sub-tasks if any (only those assigned to current user)
        if(subTasks && subTasks.length > 0) {
          const userSubTasks = subTasks.filter(st => st.assigned_to === user?.id || st.assigned_by === user?.id);
          tasksToAdd.push(...userSubTasks);
        }
        
        // Single state update to prevent race conditions
        if(tasksToAdd.length > 0) {
          // setTasks(prev => {
          //   const newTasks = [...prev];
          //   // Add tasks in order and avoid duplicates
          //   tasksToAdd.forEach(newTask => {
          //     if(!newTasks.find(t => t.id === newTask.id)) {
          //       newTasks.unshift(newTask);
          //     }
          //   });
          //   return newTasks;
          // });
//           setTasks(prev => {
//   const groupTasks = prev[group_id] || [];

//   const updatedTasks = [...groupTasks];

//   tasksToAdd.forEach(newTask => {
//     if (!updatedTasks.find(t => t.id === newTask.id)) {
//       updatedTasks.unshift(newTask);
//     }
//   });

//   return {
//     ...prev,
//     [group_id]: updatedTasks
//   };
// });
// setTasks(prev => {
//   const newTasks = [...prev];

//   tasksToAdd.forEach(newTask => {
//     if (!newTasks.find(t => t.id === newTask.id)) {
//       newTasks.unshift(newTask);
//     }
//   });

//   return newTasks;
// });

setTasks(prev => {
  console.log('[SOCKET ADD TASKS]', tasksToAdd);

  const newTasks = [...prev];

  tasksToAdd.forEach(newTask => {
    console.log('[CHECK TASK GROUP]', {
      taskId: newTask.id,
      taskGroup: newTask.group_id,
      currentGroup: group?.id
    });

    // 🚨 HARD FILTER (VERY IMPORTANT)
    if (newTask.group_id !== group?.id) {
      console.warn('[SKIPPED WRONG GROUP TASK]', newTask.id);
      return;
    }

    if (!newTasks.find(t => t.id === newTask.id)) {
      console.log('[ADDING TASK]', newTask.id);
      newTasks.unshift(newTask);
    } else {
      console.log('[DUPLICATE SKIPPED]', newTask.id);
    }
  });

  return newTasks;
});
          // Force re-render
          // setRefreshKey(prev => prev + 1);
        }
      } else {
        console.log('[TasksPanel] Task assigned to different group:', group_id, 'current group:', group?.id, '- switching to correct group');
        // Auto-switch to the group where task was assigned
        // This ensures user sees the task in its correct group
  console.log('Task for another group:', group_id);
      }
    });
    return unsub;
  },[on,user?.id,selectedTask,taskDetails]);

  // Listen for new task notifications (when tasks are created)
  useEffect(()=>{
    const unsub=on('new_message',(msg)=>{
      // Only handle task notifications
      if(msg.message_type === 'task_notification' && Number(msg.group_id) === group?.id) {
        
        // Reload tasks to get complete task data with user names
        load();
        
        // Show notification
        toast.success('New task created');
      }
    });
    return unsub;
  },[on,group?.id,load]);

  // Listen for member updates (when members are added to group)
  useEffect(()=>{
    const unsub=on('member_added',(data)=>{
      // Only handle member updates for current group
      if(Number(data.group_id) === group?.id) {
        
        // Reload group members to update the dropdown
        groupsAPI.getById(group.id).then(d => setMembers(d.members || []));
        
        // Show notification
        toast.success(`${data.added_by_name} added a new member`);
      }
    });
    return unsub;
  },[on,group?.id]);

  // Listen for member removal updates (when members are removed from group)
  useEffect(()=>{
    const unsub=on('member_removed',(data)=>{
      // Only handle member updates for current group
      if(Number(data.group_id) === group?.id) {
        
        // Reload group members to update the dropdown
        groupsAPI.getById(group.id).then(d => setMembers(d.members || []));
        
        // Show notification
        toast.success(`${data.removed_by_name} removed a member`);
      }
    });
    return unsub;
  },[on,group?.id]);

  /* ── Handle taskTarget from chat pill click ──
     If taskTarget.openForm === true: open the create form pre-set to that task type
     Also highlight the task if we have a taskId */
  useEffect(()=>{
    if(!taskTarget)return;
    const {taskId,taskType,openForm}=taskTarget;
    // Scroll to / highlight existing task
    if(taskId){
      setTimeout(()=>{
        const el=document.getElementById(`task-${taskId}`);
        if(el){
          el.scrollIntoView({behavior:'smooth',block:'center'});
          el.style.transition='box-shadow .2s';
          el.style.boxShadow='0 0 0 2px #4f7dff';
          setTimeout(()=>el.style.boxShadow='',2500);
        }
      },150);
    }
    // Open create form pre-filled with that task type
    if(openForm&&taskType){
      setForm(emptyForm(user?.role, taskType));
      setShowCreate(true);
    }
  },[taskTarget]);// re-runs each time taskTarget changes (new ts)

  const handleCreate=async()=>{
    // Prevent double submission
    if (creating) return;
    
    // Validate share_link entries
    if (form.task_type === 'share_link') {
      const validEntries = form.entries.filter(entry => 
        entry.pub_id.trim() || entry.pid.trim() || entry.link.trim()
      );
      if (validEntries.length === 0) {
        return toast.error('Please fill in at least one entry (PubID, PID, or Link)');
      }
       // 🔥 NEW VALIDATION (IMPORTANT)
  const invalidAssign = form.entries.some(entry => !entry.assigned_to || entry.assigned_to.length === 0);

  if (invalidAssign) {
    return toast.error('Please select "Assign To" for all entries');
  }
    }
    // Validate pause_pid entries
    if (form.task_type === 'pause_pid') {
      const validPauseEntries = form.pause_entries.filter(entry => 
        entry.pub_id.trim() || entry.pid.trim() || entry.pause_reason.trim() || (entry.tag && String(entry.tag).trim())
      );
      if (validPauseEntries.length === 0) {
        return toast.error('Please fill in at least one entry (PubID, PID, Pause Reason, or Tag)');
      }
       // 🔥 NEW VALIDATION (IMPORTANT)
const invalidAssign = form.pause_entries.some(entry => !entry.assigned_to || entry.assigned_to.length === 0);
  if (invalidAssign) {
    return toast.error('Please select "Assign To" for all entries');
  }
    }

      // ✅ NEW: Validate raise_request
  if (form.task_type === 'raise_request') {
    if (!form.assigned_to) {
      return toast.error('Please select "Assign To"');
    }

    if (!form.request_details || !form.request_details.trim()) {
      return toast.error('Please enter request details');
    }
  }
    // Validate optimise entries
    if (form.task_type === 'optimise') {
      const validOptimiseEntries = form.optimise_entries.filter(entry => 
        entry.pub_id.trim() || entry.pid.trim() || entry.fp.trim() || entry.fa.trim() || entry.f1.trim() || entry.f2.trim() || entry.optimise_scenario.trim()
      );
      if (validOptimiseEntries.length === 0) {
        return toast.error('Please fill in at least one entry (PubID, PID, FP, FA, F1, F2, or Scenario)');
      }
      // 🔥 NEW VALIDATION (IMPORTANT)
      const invalidAssign = form.optimise_entries.some(entry => !entry.assigned_to || entry.assigned_to.length === 0);
      if (invalidAssign) {
        return toast.error('Please select "Assign To" for all entries');
      }
    }


    setCreating(true);
    try{
      let payload;
      if(form.attachment){
        payload=new FormData();
        Object.entries({group_id:group.id,campaign_id:group.campaign_id||'',...form}).forEach(([k,v])=>{
          if(k!=='attachment'&&k!=='entries'&&k!=='pause_entries'&&k!=='optimise_entries'&&v!==null&&v!==undefined)payload.append(k,String(v));
        });
        if(form.task_type === 'share_link') payload.append('entries',JSON.stringify(form.entries));
        if(form.task_type === 'pause_pid') payload.append('pause_entries',JSON.stringify(form.pause_entries));
        if(form.task_type === 'optimise') {
          // Process optimise entries to handle attachments properly
          payload.optimise_entries = form.optimise_entries.map((entry, index) => {
            const processedEntry = {
              ...entry,
              attachment: entry.attachment, // Already contains the URL from upload
              attachment_name: entry.attachment_name
            };
            return processedEntry;
          });
          payload.append('optimise_entries',JSON.stringify(payload.optimise_entries));
        }
        // Remove direct file attachment since we're using upload endpoint
        // payload.append('attachment',form.attachment);
      }else{
        payload={group_id:group.id,campaign_id:group.campaign_id,...form};
        delete payload.attachment;
        if(form.task_type !== 'share_link') delete payload.entries;
        if(form.task_type !== 'pause_pid') delete payload.pause_entries;
        if(form.task_type !== 'optimise') delete payload.optimise_entries;
      }
      const data=await tasksAPI.create(payload);
      if(data.subTasks && data.subTasks.length > 0){
        setTasks(prev=>[...(data.task ? [data.task] : []),...prev,...data.subTasks]);
      }else{
        setTasks(prev=>[data.task,...prev]);
      }
      setShowCreate(false);
      setForm(emptyForm(user?.role));
      localStorage.removeItem(taskDraftKey(user.id, group.id));
      setHasDraft(false);
      window.dispatchEvent(new CustomEvent('task-draft-changed', { detail: { groupId: group.id } }));
      toast.success('Task created!');
    }catch(e){toast.error(e?.error||'Failed');}
    setCreating(false);
  };

  const handleStatusUpdate=async(taskId,status,comment,feedback)=>{
    try {
      const data = await tasksAPI.updateStatus(taskId,status,comment,feedback);
      const updated = data?.task || {};
      setTasks(prev=>prev.map(t=>t.id===taskId?{
        ...t,
        status,
        rejection_note: updated.rejection_note ?? t.rejection_note,
        rejected_by_name: updated.rejected_by_name ?? t.rejected_by_name,
        completion_note: updated.completion_note ?? t.completion_note,
        completed_by_name: updated.completed_by_name ?? t.completed_by_name,
      }:t));
      window.dispatchEvent(new CustomEvent('task-status-changed', { detail: { groupId: group.id } }));
    } catch (error) {
      if (error.error === 'Access Denied: Only assigned user can update task status') {
        toast.error('Access Denied: Only assigned users can update task status');
      } else if (error.error && error.error !== 'Server error') {
        // Validation messages (e.g. missing rejection reason / feedback) are safe to show
        toast.error(error.error);
      } else {
        toast.error('Failed to update task status');
      }
      throw error; // Re-throw to let TaskItem handle it
    }
  };

  const handleFollowup=async(task, message)=>{
    try {
      const data = await tasksAPI.createFollowup({group_id:group.id, task_id:task.id, message});
      // Add to local followups so it shows immediately without a reload
      setFollowups(prev=>[...prev,{
        id: data.followup_id,
        task_id: task.id,
        group_id: group.id,
        created_by: user.id,
        created_by_name: user.full_name,
        recipient_id: task.assigned_by===user.id ? task.assigned_to : task.assigned_by,
        recipient_name: task.assigned_by===user.id ? task.assigned_to_name : task.assigned_by_name,
        message,
        created_at: new Date().toISOString(),
      }]);
      toast.success('Follow-up sent!');
    } catch {
      toast.error('Failed to send follow-up');
      throw new Error('failed');
    }
  };

  const handleTaskClick = async (taskId) => {
    if (selectedTask === taskId) {
      setSelectedTask(null);
      setTaskDetails(null);
      return;
    }
    
    setSelectedTask(taskId);
    setLoadingDetails(true);
    try {
      const data = await tasksAPI.getById(taskId);
      setTaskDetails(data.task);
    } catch (error) {
      if (error.error === 'Access Denied: Only assigned user can view task details') {
        toast.error('Access Denied: You can only view tasks assigned to you');
      } else {
        toast.error('Failed to load task details');
      }
      setSelectedTask(null);
      setTaskDetails(null);
    } finally {
      setLoadingDetails(false);
    }
  };

  // const filtered=tasks.filter(t=>filter==='all'?true:t.status===filter);
  // const filtered = tasks
  // .filter(t => t.group_id === group?.id) // ✅ CRITICAL FIX
  // .filter(t => filter === 'all' ? true : t.status === filter);
  const filtered = tasks
  .filter(t => {
    const match = t.group_id === group?.id;
    if (!match) console.warn('[FILTER REMOVED WRONG TASK]', { taskId: t.id, taskGroup: t.group_id, currentGroup: group?.id });
    return match;
  })
  .filter(t => filter === 'all' ? true : t.status === filter)
  .filter(t => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return [
      TASK_TYPES[t.task_type]?.label, t.description, t.pub_id, t.pid,
      t.link, t.assigned_by_name, t.assigned_to_name, t.pause_reason,
      t.optimise_scenario, t.request_type, t.request_details, t.fp, t.geo,
    ].some(v => v && String(v).toLowerCase().includes(q));
  });
  const pendingCount=tasks.filter(t=>t.status==='pending').length;



   const [openIndex, setOpenIndex] = useState(null);
  const [tempLink, setTempLink] = useState("");
  const wrapperRef = useRef(null);
const [popupPos, setPopupPos] = useState({ top: 0, left: 0 });
const openEditor = (entry, index, e) => {
  const rect = e.target.getBoundingClientRect();
  setTempLink(entry.link || "");
  setOpenIndex(index);
  const popupW = Math.min(300, window.innerWidth - 16), popupH = 160, margin = 8;
  const left = Math.min(rect.left, window.innerWidth - popupW - margin);
  const spaceBelow = window.innerHeight - rect.bottom - margin;
  const top = spaceBelow >= popupH ? rect.bottom + 5 : rect.top - popupH - 5;
  setPopupPos({ top: Math.max(margin, top), left: Math.max(margin, left) });
};
const saveLink = () => {
  updateEntry(openIndex, "link", tempLink); // ✅ use state
  setOpenIndex(null);
};

  const [openNoteIndex, setOpenNoteIndex] = useState(null);
  const [tempNote, setTempNote] = useState("");
  const noteWrapperRef = useRef(null);
  const [notePopupPos, setNotePopupPos] = useState({ top: 0, left: 0 });
  const openOptimiseNoteEditor = (entry, index, e) => {
    const rect = e.target.getBoundingClientRect();
    setTempNote(entry.note || "");
    setOpenNoteIndex(index);
    const popupW = 300, popupH = 160, margin = 8;
    const left = Math.min(rect.left, window.innerWidth - popupW - margin);
    const spaceBelow = window.innerHeight - rect.bottom - margin;
    const top = spaceBelow >= popupH ? rect.bottom + 5 : rect.top - popupH - 5;
    setNotePopupPos({ top: Math.max(margin, top), left: Math.max(margin, left) });
  };
  const saveOptimiseNote = () => {
    updateOptimiseEntry(openNoteIndex, "note", tempNote);
    setOpenNoteIndex(null);
  };

  
  return(
    <>
    {/* <div style={{fontSize:10, background:'#000', color:'#0f0', padding:6}}>
  <div>Current Group: {group?.id}</div>
  <div>Total Tasks: {tasks.length}</div>
  <div>
    Tasks Groups: {tasks.map(t => t.group_id).join(', ')}
  </div>
</div> */}
    <div style={{display:'flex',flexDirection:'column',height:'100%'}}>

      {/* filter bar */}
      <div style={{display:'flex',gap:6,padding:'10px 14px',borderBottom:'1px solid var(--border)',flexWrap:'wrap',alignItems:'center'}}>
        {['pending','accepted','completed','rejected'].map(f2=>(
          <button key={f2} onClick={()=>setFilter(f2)}
            className={`btn btn-xs ${filter===f2?'btn-primary':'btn-secondary'}`}
            style={{textTransform:'capitalize'}}>
            {f2}
            {f2==='pending'&&pendingCount>0&&(
              <span style={{marginLeft:4,background:'#ef4444',color:'white',borderRadius:10,padding:'0 5px',fontSize:9,fontWeight:700}}>{pendingCount}</span>
            )}
          </button>
        ))}
        <button className="btn btn-xs btn-primary" style={{marginLeft:'auto'}} onClick={() => setShowCreate(x => !x)}>
          {showCreate?'✕ Cancel':'+ New Task'}
        </button>
      </div>

      {/* CREATE FORM */}
      {showCreate&&(
        <div style={{padding:16,borderBottom:'1px solid var(--border)',background:'var(--bg-tertiary)',overflowY:'auto',maxHeight:500}}>

          {/* Discard draft button */}
          {hasDraft && (
            <div style={{display:'flex',justifyContent:'flex-end',marginBottom:8}}>
              <button type="button" className="btn btn-xs btn-danger"
                onClick={() => setForm(emptyForm(user?.role))}
                style={{fontSize:11,opacity:0.8}}>
                🗑 Discard Draft
              </button>
            </div>
          )}

          {/* banner for share_link pre-fill */}
          {form.task_type==='share_link'&&(
            <div style={{marginBottom:12,padding:'8px 12px',background:'rgba(79,125,255,.1)',borderRadius:8,border:'1px solid rgba(79,125,255,.3)',fontSize:12,color:'#4f7dff',fontWeight:600}}>
              🔗 Fill in the campaign link details and assign to a publisher
            </div>
          )}

          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10,marginBottom:10}}>
            <div>
              <label className="form-label">Task Type</label>
              <select className="form-control" value={form.task_type} onChange={e=>f('task_type',e.target.value)}>
                {getVisibleActions(user?.role).map(action => {
                  const taskType = TASK_TYPES[action];
                  return (
                    <option key={action} value={action}>{taskType.icon} {taskType.label}</option>
                  );
                })}
              </select>
            </div>
            {/* <div>
              <label className="form-label">Assign To</label>
              <select className="form-control" value={form.assigned_to} onChange={e=>f('assigned_to',e.target.value)}>
                <option value="">Unassigned</option>
                {users.map(u=><option key={u.id} value={u.id}>{u.full_name} ({u.role})</option>)}
              </select>
            </div> */}
          </div>

          {/* Share Link */}
          {form.task_type==='share_link'&&(
            <div style={{padding:'8px 10px',background:'rgba(79,125,255,0.1)',borderRadius:6,
              border:'1px solid rgba(79,125,255,0.2)',marginBottom:10}}>
              <div style={{fontSize:10,fontWeight:600,marginBottom:6}}>🔗 Link Details</div>
              {/* Unified scroll: header + entries scroll together */}
              <div style={{overflowX:'auto',overflowY:'auto',maxHeight:220,WebkitOverflowScrolling:'touch'}}>
              {/* Table Header — sticky so it stays visible when scrolling vertically */}
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1.5fr 1fr 1.2fr 1.2fr auto',gap:4,marginBottom:4,fontSize:10,fontWeight:600,minWidth:480,position:'sticky',top:0,background:'rgba(79,125,255,0.18)',zIndex:1,padding:'4px 2px',borderRadius:4}}>
                <div>Assign To</div>
                <div>PubID</div>
                <div>PID</div>
                <div>Link</div>
                <div>GEO</div>
                <div>Note</div>
                <div></div>
              </div>

              {/* Entries — no separate Y scroll, parent handles it */}
                {form.entries.map((entry, index) => (
                  <div key={index} style={{display:'grid',gridTemplateColumns:'1fr 1fr 1.5fr 1fr 1.2fr 1.2fr auto',gap:4,marginBottom:6,minWidth:480}}>
                    {/* <select 
                      className="form-control" 
                      style={{fontSize:11,padding:4,background:'rgba(255,255,255,0.1)',border:'1px solid rgba(255,255,255,0.2)',color:'#fff'}}
                      value={entry.assigned_to} 
                      onChange={e => updateEntry(index, 'assigned_to', e.target.value)}
                    >
                      <option value="">Unassigned</option>
                      {getFilteredMembersForTaskType('share_link').map(member => <option key={member.id} value={member.id}>{member.full_name}</option>)}
                    </select> */}
                    <div style={{position:'relative'}}>
                      <button type="button"
                        onClick={e=>openAssignee(e,`sl-${index}`)}
                        style={{width:'100%',fontSize:11,padding:4,background:'var(--bg-hover)',border:(entry.assigned_to||[]).length===0?'1px solid #ef4444':'1px solid var(--border)',color:'var(--text-primary)',borderRadius:4,cursor:'pointer',textAlign:'left',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                        {assigneeLabel(entry.assigned_to)}
                      </button>
                      {openAssigneeKey===`sl-${index}`&&(
                        <div onMouseDown={e=>e.stopPropagation()} style={{position:'fixed',top:dropdownPos.top,left:dropdownPos.left,minWidth:Math.max(dropdownPos.width,160),zIndex:9999,background:'var(--bg-secondary)',border:'1px solid var(--border)',borderRadius:6,padding:4,boxShadow:'0 4px 16px rgba(0,0,0,0.15)',display:'flex',flexDirection:'column',maxHeight:220}}>
                          <input autoFocus value={assigneeSearch} onChange={e=>setAssigneeSearch(e.target.value)} placeholder="Search…" onMouseDown={e=>e.stopPropagation()} style={{fontSize:11,padding:'3px 6px',marginBottom:4,border:'1px solid var(--border)',borderRadius:4,background:'var(--bg-primary)',color:'var(--text-primary)',outline:'none',flexShrink:0}}/>
                          <div style={{overflowY:'auto',flex:1}}>
                          {getFilteredMembersForTaskType('share_link').filter(u=>u.full_name.toLowerCase().includes(assigneeSearch.toLowerCase())).map(u=>(
                            <label key={u.id} style={{display:'flex',alignItems:'center',gap:6,padding:'3px 6px',cursor:'pointer',borderRadius:4,fontSize:11,color:'var(--text-primary)'}}>
                              <input type="checkbox" checked={(entry.assigned_to||[]).includes(Number(u.id))}
                                onChange={()=>updateEntry(index,'assigned_to',toggleAssignee(entry.assigned_to||[],u.id))}/>
                              {u.full_name}
                            </label>
                          ))}
                          </div>
                        </div>
                      )}
                    </div>
                    <input 
                      className="form-control" 
                      style={{fontSize:11,padding:4,background:'rgba(255,255,255,0.1)',border:'1px solid rgba(255,255,255,0.2)'}} 
                      placeholder="PubID" 
                      value={entry.pub_id} 
                      onChange={e => updateEntry(index, 'pub_id', e.target.value)}
                    />
                    <input 
                      className="form-control" 
                      style={{fontSize:11,padding:4,background:'rgba(255,255,255,0.1)',border:'1px solid rgba(255,255,255,0.2)'}} 
                      placeholder="PID" 
                      value={entry.pid} 
                      onChange={e => updateEntry(index, 'pid', e.target.value)}
                    />
                      <div style={{ position: "relative" }}>
      {/* Always same size input */}
      <input
        className="form-control"
        style={{
          fontSize: 11,
          padding: 6,
          background: "rgba(255,255,255,0.1)",
          border: "1px solid rgba(255,255,255,0.2)",
          cursor: "pointer"
        }}
        value={entry.link}
        placeholder="Click to add link"
        readOnly
// onClick={() => openEditor(entry, index)}      />
onClick={(e) => openEditor(entry, index, e)}/>

      {/* Floating Editor */}
     {openIndex === index && (
  <div
    ref={wrapperRef}
    style={{
      position: "fixed",
      top: popupPos.top,
      left: popupPos.left,
      width: Math.min(300, window.innerWidth - 16),
      background: "#1f1f1f",
      border: "1px solid #333",
      borderRadius: 8,
      padding: 10,
      zIndex: 9999,
      boxShadow: "0 8px 20px rgba(0,0,0,0.4)"
    }}
  >
          <textarea
            value={tempLink}
            onChange={(e) => setTempLink(e.target.value)}
            style={{
              width: "100%",
              height: 100,
              resize: "none",
              borderRadius: 6,
              border: "1px solid #444",
              padding: 6,
              background: "#111",
              color: "#fff"
            }}
          />

          <div style={{ marginTop: 8, textAlign: "right" }}>
            <button
              onClick={saveLink}
              style={{
                marginRight: 6,
                padding: "4px 10px",
                background: "#1677ff",
                color: "#fff",
                border: "none",
                borderRadius: 4
              }}
            >
              Save
            </button>
            <button
              onClick={() => setOpenIndex(null)}
              style={{
                padding: "4px 10px",
                background: "#444",
                color: "#fff",
                border: "none",
                borderRadius: 4
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
                    {/* <input 
                      className="form-control" 
                      style={{fontSize:11,padding:4,background:'rgba(255,255,255,0.1)',border:'1px solid rgba(255,255,255,0.2)'}} 
                      placeholder="Link" 
                      value={entry.link} 
                      onChange={e => updateEntry(index, 'link', e.target.value)}
                    /> */}
                    <input 
                      className="form-control" 
                      style={{fontSize:11,padding:4,background:'rgba(255,255,255,0.1)',border:'1px solid rgba(255,255,255,0.2)'}} 
                      placeholder="GEO" 
                      value={entry.geo || ''} 
                      onChange={e => updateEntry(index, 'geo', e.target.value)}
                    />
                    <input 
                      className="form-control" 
                      style={{fontSize:11,padding:4,background:'rgba(255,255,255,0.1)',border:'1px solid rgba(255,255,255,0.2)'}} 
                      placeholder="Note" 
                      value={entry.note} 
                      onChange={e => updateEntry(index, 'note', e.target.value)}
                    />
                    {index > 0 ? (
                      <button
                        type="button"
                        onClick={() => removeEntry(index)}
                        style={{
                          background:'rgba(239,68,68,0.2)',
                          border:'1px solid rgba(239,68,68,0.3)',
                          color:'#ef4444',
                          borderRadius:'4px',
                          padding:'4px 8px',
                          fontSize:'12px',
                          cursor:'pointer',
                          transition:'all 0.15s'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background='rgba(239,68,68,0.3)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background='rgba(239,68,68,0.2)';
                        }}
                      >
                        ✕
                      </button>
                    ) : <div />}
                  </div>
                ))}

              {/* Add Entry Button */}
              <button
                    type="button"
                    onClick={addEntry}
                    style={{
                      background:'rgba(79,125,255,0.2)',
                      border:'1px solid rgba(79,125,255,0.3)',
                      color:'#4f7dff',
                      borderRadius:'6px',
                      padding:'6px 12px',
                      fontSize:'11px',
                      cursor:'pointer',
                      transition:'all 0.15s',
                      display:'flex',
                      alignItems:'center',
                      gap:'6px',
                      marginTop:'8px'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background='rgba(79,125,255,0.3)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background='rgba(79,125,255,0.2)';
                    }}
                  >
                    ➕ Add Entry
                  </button>
              </div>{/* end overflowX scroll wrapper */}
            </div>
          )}

          {/* Pause PID */}
          {form.task_type==='pause_pid'&&(
            <div style={{padding:'8px 10px',background:'rgba(245,158,11,0.1)',borderRadius:6,
              border:'1px solid rgba(245,158,11,0.2)',marginBottom:10}}>
              <div style={{fontSize:10,fontWeight:600,marginBottom:6}}>⏸️ Pause Details</div>
              
              {/* Unified scroll: header + entries scroll together */}
              <div style={{overflowX:'auto',overflowY:'auto',maxHeight:220,WebkitOverflowScrolling:'touch'}}>
              {/* Table Header — sticky */}
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr 1fr 1fr 1fr 1fr auto',gap:4,marginBottom:4,fontSize:10,fontWeight:600,minWidth:600,position:'sticky',top:0,background:'rgba(245,158,11,0.18)',zIndex:1,padding:'4px 2px',borderRadius:4}}>
                <div>Assign To</div>
                <div>Tag</div>
                <div>PubID</div>
                <div>PID</div>
                <div>GEO</div>
                <div>Pause Scenario</div>
                <div>Note</div>
                <div></div>
              </div>

                {form.pause_entries.map((entry, index) => (
                  <div key={index} style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr 1fr 1fr 1fr 1fr auto',gap:4,marginBottom:6,minWidth:600}}>
                    {/* <select 
                      className="form-control" 
                      style={{fontSize:11,padding:4,background:'rgba(255,255,255,0.1)',border:'1px solid rgba(255,255,255,0.2)',color:'#fff'}}
                      value={entry.assigned_to} 
                      onChange={e => updatePauseEntry(index, 'assigned_to', e.target.value)}
                    >
                      <option value="">Unassigned</option>
                      {getFilteredMembersForTaskType('pause_pid').map(member => <option key={member.id} value={member.id}>{member.full_name}</option>)}
                    </select> */}
                    <div style={{position:'relative'}}>
                      <button type="button"
                        onClick={e=>openAssignee(e,`pp-${index}`)}
                        style={{width:'100%',fontSize:11,padding:4,background:'var(--bg-hover)',border:(entry.assigned_to||[]).length===0?'1px solid #ef4444':'1px solid var(--border)',color:'var(--text-primary)',borderRadius:4,cursor:'pointer',textAlign:'left',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                        {assigneeLabel(entry.assigned_to)}
                      </button>
                      {openAssigneeKey===`pp-${index}`&&(
                        <div onMouseDown={e=>e.stopPropagation()} style={{position:'fixed',top:dropdownPos.top,left:dropdownPos.left,minWidth:Math.max(dropdownPos.width,160),zIndex:9999,background:'var(--bg-secondary)',border:'1px solid var(--border)',borderRadius:6,padding:4,boxShadow:'0 4px 16px rgba(0,0,0,0.15)',display:'flex',flexDirection:'column',maxHeight:220}}>
                          <input autoFocus value={assigneeSearch} onChange={e=>setAssigneeSearch(e.target.value)} placeholder="Search…" onMouseDown={e=>e.stopPropagation()} style={{fontSize:11,padding:'3px 6px',marginBottom:4,border:'1px solid var(--border)',borderRadius:4,background:'var(--bg-primary)',color:'var(--text-primary)',outline:'none',flexShrink:0}}/>
                          <div style={{overflowY:'auto',flex:1}}>
                          {getFilteredMembersForTaskType('pause_pid').filter(u=>u.full_name.toLowerCase().includes(assigneeSearch.toLowerCase())).map(u=>(
                            <label key={u.id} style={{display:'flex',alignItems:'center',gap:6,padding:'3px 6px',cursor:'pointer',borderRadius:4,fontSize:11,color:'var(--text-primary)'}}>
                              <input type="checkbox" checked={(entry.assigned_to||[]).includes(Number(u.id))}
                                onChange={()=>updatePauseEntry(index,'assigned_to',toggleAssignee(entry.assigned_to||[],u.id))}/>
                              {u.full_name}
                            </label>
                          ))}
                          </div>
                        </div>
                      )}
                    </div>
                    <select
                      className="form-control"
                      style={{fontSize:11,padding:4,background:'rgba(255,255,255,0.1)',border:'1px solid rgba(255,255,255,0.2)'}}
                      value={entry.tag || ''}
                      onChange={e => updatePauseEntry(index, 'tag', e.target.value)}
                    >
                      <option value="">Select Tag…</option>
                      {members.filter(m => ['publisher', 'publisher_manager', 'pub_executive'].includes(m.role)).map(member => (
                        <option key={member.id} value={member.id}>{member.full_name}</option>
                      ))}
                    </select>
                    <input 
                      className="form-control" 
                      style={{fontSize:11,padding:4,background:'rgba(255,255,255,0.1)',border:'1px solid rgba(255,255,255,0.2)'}} 
                      placeholder="PubID" 
                      value={entry.pub_id} 
                      onChange={e => updatePauseEntry(index, 'pub_id', e.target.value)}
                    />
                    <input 
                      className="form-control" 
                      style={{fontSize:11,padding:4,background:'rgba(255,255,255,0.1)',border:'1px solid rgba(255,255,255,0.2)'}} 
                      placeholder="PID" 
                      value={entry.pid} 
                      onChange={e => updatePauseEntry(index, 'pid', e.target.value)}
                    />
                       <input 
                      className="form-control" 
                      style={{fontSize:11,padding:4,background:'rgba(255,255,255,0.1)',border:'1px solid rgba(255,255,255,0.2)'}} 
                      placeholder="GEO" 
                      value={entry.geo || ''} 
                      onChange={e => updatePauseEntry(index, 'geo', e.target.value)}
                    />
                    <select
                      className="form-control"
                      style={{fontSize:11,padding:4,background:'rgba(255,255,255,0.1)',border:'1px solid rgba(255,255,255,0.2)'}}
                      value={entry.pause_reason}
                      onChange={e => updatePauseEntry(index, 'pause_reason', e.target.value)}
                    >
                      <option value="">Select scenario…</option>
                      {PAUSE_SCENARIOS.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <input
                      className="form-control"
                      style={{fontSize:11,padding:4,background:'rgba(255,255,255,0.1)',border:'1px solid rgba(255,255,255,0.2)'}}
                      placeholder="Note"
                      value={entry.note || ''}
                      onChange={e => updatePauseEntry(index, 'note', e.target.value)}
                    />
                    {index > 0 ? (
                      <button
                        type="button"
                        onClick={() => removePauseEntry(index)}
                        style={{
                          background:'rgba(239,68,68,0.2)',
                          border:'1px solid rgba(239,68,68,0.3)',
                          color:'#ef4444',
                          borderRadius:'4px',
                          padding:'4px 8px',
                          fontSize:'12px',
                          cursor:'pointer',
                          transition:'all 0.15s'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background='rgba(239,68,68,0.3)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background='rgba(239,68,68,0.2)';
                        }}
                      >
                        ✕
                      </button>
                    ) : <div />}
                  </div>
                ))}

              {/* Add Entry Button */}
              <button
                type="button"
                onClick={addPauseEntry}
                style={{
                  background:'rgba(245,158,11,0.2)',
                  border:'1px solid rgba(245,158,11,0.3)',
                  color:'#f59e0b',
                  borderRadius:'6px',
                  padding:'6px 12px',
                  fontSize:'11px',
                  cursor:'pointer',
                  transition:'all 0.15s',
                  display:'flex',
                  alignItems:'center',
                  gap:'6px',
                  marginTop:'8px'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background='rgba(245,158,11,0.3)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background='rgba(245,158,11,0.2)';
                }}
              >
                ➕ Add Entry
              </button>
              </div>{/* end overflowX scroll wrapper */}
            </div>
          )}

          {/* Raise Request */}
          {form.task_type==='raise_request'&&(
            <>
              <div style={{marginBottom:10}}>
                <label className="form-label">Assign To</label>
                {/* <select className="form-control" value={form.assigned_to} onChange={e=>f('assigned_to',e.target.value)}>
                  <option value="">Unassigned</option>
                  {getFilteredMembersForTaskType('raise_request').map(member => <option key={member.id} value={member.id}>{member.full_name}</option>)}
                </select> */}
                <select 
  className="form-control" 
  style={{
    border: !form.assigned_to 
      ? '1px solid #ef4444' 
      : '1px solid var(--border)'
  }}
  value={form.assigned_to || ""} 
  onChange={e => f('assigned_to', e.target.value)}
  required
>

  <option value="">Select User *</option>
  {getFilteredMembersForTaskType('raise_request').map(member => (
    <option key={member.id} value={member.id}>
      {member.full_name}
    </option>
  ))}
</select>
              </div>
              <div style={{marginBottom:10}}>
                <label className="form-label">Request Type</label>
                <select className="form-control" value={form.request_type} onChange={e=>f('request_type',e.target.value)}>
                  {REQUEST_TYPES.map(t=><option key={t} value={t}>{t.toUpperCase()}</option>)}
                </select>
              </div>
              <div style={{marginBottom:10}}>
                <label className="form-label">Cap Management</label>
                <select className="form-control" value={form.cap_management} onChange={e=>f('cap_management',e.target.value)}>
                  <option value="">Select Cap Management</option>
                  <option value="Increase Cap – Installs">Increase Cap – Installs</option>
                  <option value="Increase Cap – Clicks">Increase Cap – Clicks</option>
                  <option value="Increase Cap – Events">Increase Cap – Events</option>
                  <option value="Decrease Cap – Installs">Decrease Cap – Installs</option>
                  <option value="Decrease Cap – Clicks">Decrease Cap – Clicks</option>
                  <option value="Decrease Cap – Events">Decrease Cap – Events</option>
                  <option value="Increase PO">Increase PO</option>
                  <option value="Decrease PO">Decrease PO</option>
                </select>
              </div>
              <div style={{marginBottom:10}}>
                <label className="form-label">Details</label>
                <textarea className="form-control" style={{minHeight:60}} value={form.request_details} onChange={e=>f('request_details',e.target.value)} placeholder="Describe…"/>
              </div>
            </>
          )}

          {/* Optimise */}
       {form.task_type === 'optimise' && (() => {
  const userFields = OPTIMISE_FIELDS[user?.role] || OPTIMISE_FIELDS.am;

  const renderHeader = (field) => {
    const labels = {
      assigned_to: 'Assign To',
      pub_id: 'PubID',
      pid: 'PID',
      fp: 'FP',
      fa: 'FA',
      f1: 'F1',
      f2: 'F2',
      optimise_scenario: 'Scenario',
      attachment: 'Attachment',
      note: 'Note'
    };
    return (
      <div key={field} style={{fontSize:10,fontWeight:500}}>
        {labels[field]}
      </div>
    );
  };

  return (
    <div style={{
      padding:'8px 10px',
      background:'rgba(6,182,212,0.1)',
      borderRadius:6,
      border:'1px solid rgba(6,182,212,0.2)',
      marginBottom:10
    }}>
      <div style={{
        fontSize:10,
        fontWeight:600,
        marginBottom:6
      }}>
        ⚡ Optimise
      </div>

      <div style={{overflowX:'auto',overflowY:'auto',maxHeight:220,WebkitOverflowScrolling:'touch'}}>
        {/* Header */}
        <div style={{
          display:'grid',
          gridTemplateColumns:`repeat(${userFields.length}, 1fr) auto`,
          gap:4,
          marginBottom:4,
          minWidth:480,
          position:'sticky',
          top:0,
          background:'rgba(6,182,212,0.18)',
          zIndex:1,
          padding:'4px 2px',
          borderRadius:4
        }}>
          {userFields.map(renderHeader)}
          <div></div>
        </div>

        {/* Rows */}
        {form.optimise_entries.map((entry, index) => (
          <div key={index} style={{
            display:'grid',
            gridTemplateColumns:`repeat(${userFields.length}, 1fr) auto`,
            gap:4,
            marginBottom:6,
            minWidth:480
          }}>
            {userFields.map(field =>
              renderEntryField(field, entry, index)
            )}

            {index > 0 ? (
              <button
                type="button"
                onClick={() => removeOptimiseEntry(index)}
                style={{
                  background:'rgba(239,68,68,0.2)',
                  border:'1px solid rgba(239,68,68,0.3)',
                  color:'#ef4444',
                  borderRadius:'4px',
                  padding:'4px 8px',
                  fontSize:'12px',
                  cursor:'pointer'
                }}
              >
                ✕
              </button>
            ) : <div />}
          </div>
        ))}
      </div>

      {/* Add Row */}
      <button
        type="button"
        onClick={addOptimiseEntry}
        style={{
          background:'rgba(6,182,212,0.2)',
          border:'1px solid rgba(6,182,212,0.3)',
          color:'#06b6d4',
          borderRadius:'6px',
          padding:'6px 12px',
          fontSize:'11px',
          cursor:'pointer',
          display:'flex',
          alignItems:'center',
          gap:'6px',
          marginTop:'8px'
        }}
      >
        ➕ Add Entry
      </button>
    </div>
  );
})()}

          <button className="btn btn-primary" style={{width:'100%',marginTop:4}} onClick={handleCreate} disabled={creating}>
            {creating?'Creating…':'✓ Create Task'}
          </button>
        </div>
      )}

      {/* task list */}
      <div style={{flex:1,overflowY:'auto',padding:'10px 12px'}}>
        {loadingTasks ? (
          // Loading state
          <div style={{display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',padding:'40px',color:'var(--text-muted)'}}>
            <div style={{
              width:'32px',
              height:'32px',
              border:'3px solid var(--border)',
              borderTop:'3px solid var(--accent)',
              borderRadius:'50%',
              animation:'spin 1s linear infinite',
              marginBottom:'12px'
            }}></div>
            <p style={{margin:0,fontSize:'14px'}}>Loading tasks...</p>
          </div>
        ) : filtered.length===0 ? (
          // Empty state
          <div className="empty-state" style={{padding:40,textAlign:'center',color:'var(--text-muted)'}}>
            <div style={{fontSize:'32px',marginBottom:'8px'}}>📋</div>
            <p style={{margin:0,fontSize:'14px'}}>
              {tasks.length===0 ? 'No tasks found' : `No ${filter!=='all'?filter:''} tasks`}
            </p>
          </div>
        ) : (
          // key={`${task.id}-${refreshKey}`}
          // Task list
          filtered.map(task=>(
            <div id={`task-${task.id}`} >
              <TaskItem 
                // key={`${task.id}-${refreshKey}`} 
                task={task} 
                currentUser={user} 
                onStatusUpdate={handleStatusUpdate} 
                onFollowup={handleFollowup}
                onTaskClick={handleTaskClick}
                followups={followups}
                group={group}
              />
            </div>
          ))
        )}
      </div>
    </div>

    {/* Task Details Modal */}
    <TaskDetailsModal 
      task={taskDetails} 
      onClose={() => {
        setSelectedTask(null);
        setTaskDetails(null);
      }}
      loading={loadingDetails}
    />
    
    </>
    
  );
}
